import { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Printer, ArrowRight } from "lucide-react";
import { REPRINT_REASONS } from "@/lib/reprintReasons";
import { otherMedia, mediaLabelOf } from "@/lib/tagMedia";

// Tap a preset reason and go — no typing unless "Other". Shared by the part-tag
// reprint and the Make-Tags range reprint so the audit trail stays consistent.
// When "Wrong media for the part" is picked, it turns the observation into a FIX:
// it offers the OTHER media right here so the correct label prints in the same tap.
export function ReprintReasonPicker({ open, onOpenChange, onConfirm, count, currentMedia }) {
  const [reason, setReason] = useState(null);
  const [text, setText] = useState("");
  const [target, setTarget] = useState(null);   // wrong-media fix: which media to print instead

  const alts = otherMedia(currentMedia);
  const isWrongMedia = reason === "wrong_media";
  const canConfirm = reason
    && (reason !== "other" || text.trim().length >= 2)
    && (!isWrongMedia || !!target);

  const reset = () => { setReason(null); setText(""); setTarget(null); };
  const pickReason = (code) => {
    setReason(code);
    // auto-select the only alternative media so the common case is a single tap
    if (code === "wrong_media") setTarget(alts.length === 1 ? alts[0].code : null);
    else setTarget(null);
  };
  const confirm = () => {
    if (!canConfirm) return;
    // printMedia defaults to the current media; the wrong-media fix redirects it.
    onConfirm(reason, reason === "other" ? text.trim() : "", isWrongMedia ? target : currentMedia);
    reset();
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) reset(); onOpenChange(v); }}>
      <DialogContent className="rounded-lg border-slate-200 sm:max-w-md" data-testid="reprint-reason-picker">
        <DialogHeader>
          <DialogTitle className="font-display flex items-center gap-2"><Printer className="w-5 h-5 text-[#B01E28]" />Why the reprint?</DialogTitle>
        </DialogHeader>
        <p className="text-xs text-slate-500">
          Reprinting makes another physical label{count ? ` for ${count} tag(s)` : ""}
          {currentMedia ? ` as ${mediaLabelOf(currentMedia)}` : ""} for the same number(s). Pick a reason — it’s logged.
        </p>
        <div className="flex flex-wrap gap-2 py-1">
          {REPRINT_REASONS.map((r) => (
            <button key={r.code} data-testid={`reason-${r.code}`} onClick={() => pickReason(r.code)}
              className={`rounded-full px-4 py-2.5 font-semibold text-sm border-2 transition-colors ${reason === r.code ? "border-[#B01E28] bg-red-50 text-[#B01E28]" : "border-slate-200 text-slate-600 hover:border-[#B01E28]"}`}>
              {r.label}
            </button>
          ))}
        </div>

        {reason === "other" && (
          <Input data-testid="reason-other-text" autoFocus value={text} onChange={(e) => setText(e.target.value)}
            placeholder="Briefly, what happened?" className="rounded-lg" />
        )}

        {isWrongMedia && (
          <div data-testid="wrong-media-fix" className="rounded-lg bg-amber-50 border border-amber-200 p-3">
            <div className="text-xs font-semibold text-amber-900 mb-2 flex items-center gap-1.5">
              Print on the correct media instead <ArrowRight className="w-3.5 h-3.5" />
            </div>
            <div className="flex flex-wrap gap-2">
              {alts.map((m) => (
                <button key={m.code} data-testid={`fix-media-${m.code}`} onClick={() => setTarget(m.code)}
                  className={`rounded-lg px-4 py-2.5 font-semibold text-sm border-2 transition-colors ${target === m.code ? "border-amber-600 bg-white text-amber-800" : "border-amber-200 text-amber-700 hover:border-amber-500"}`}>
                  {m.label}
                </button>
              ))}
            </div>
            <p className="text-[11px] text-amber-700 mt-2">Same number — this just prints it on the right media. No voiding, no new number.</p>
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" className="rounded-lg border-slate-200" onClick={() => { reset(); onOpenChange(false); }}>Cancel</Button>
          <Button data-testid="reason-confirm" disabled={!canConfirm} className="rounded-lg bg-[#B01E28] hover:bg-[#8f1820] text-white" onClick={confirm}>
            <Printer className="w-4 h-4 mr-1" />
            {isWrongMedia && target ? `Print as ${mediaLabelOf(target)}` : "Print"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
